<template>
  <div>
    <BankAccountDeleteModal v-if="selectedAccount" :show-modal="showDeleteModal" @close="showDeleteModal = false" @account-deleted="showDeleteModal = false" />
    <BankConnectionModal :show-modal="showModal" @close="showModal = false" :existingBeIban="existingBeIban" />
    <TitleBar>
      <template v-slot:title>{{ $gettext('Bank Transactions') }}</template>
      <template v-slot:buttons>
        <OnboardingIntroLink :onboarding-intro="'onboarding_web_bank'" />

        <GekkoButton 
          v-if="showWarningButton && !showFailureMessage"
          @click="showModal = true"
          :text="$gettext('Your bank connection is about to expire! Click to reconnect')"
          icon="fa-solid fa-triangle-exclamation"
          variant="link"
          class="text-danger"
        />

        <GekkoButton 
          v-if="showFailureMessage"
          @click="showModal = true"
          :text="$gettext('Your connection request failed! Click to reconnect')"
          icon="fa-solid fa-triangle-exclamation"
          variant="link"
          class="text-danger"
        />

        <GekkoButton 
          v-if="showRefreshMessage"
          :text="$gettext('We requested an update on your bank account transactions')"
          icon="fa-solid fa-arrows-rotate"
          variant="link"
          class="text-warning"
        />

        <GekkoButton 
          @click="tryUpdateTransactions" 
          :text="$gettext('Update transactions')"
          :disabled="!selectedAccount"
          class="mr-2"
        />

        <b-dropdown class="icon-toggle ui-dashboard-menu-dropdown" variant="outline-primary" right no-caret>
          <template #button-content>
            <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
          </template>
          <b-dropdown-item :href="'/reports/#/cashflow/'">
            <font-awesome-icon icon="fa-solid fa-chart-column" />
            <span v-translate>Cash flow report</span>
          </b-dropdown-item>
          <b-dropdown-item @click="exportAsExcel()">
            <font-awesome-icon icon="fa-solid fa-download" />
            <span v-translate>Export as Excel</span>
          </b-dropdown-item>
          <b-dropdown-item @click="showModal = true">
            <font-awesome-icon icon="fa-solid fa-plus" />
            <span v-translate>Add bank account</span>
          </b-dropdown-item>
          <b-dropdown-item @click="showDeleteModal = true">
            <font-awesome-icon icon="fa-solid fa-trash-can" />
            <span v-translate>Delete bank account</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </TitleBar>
    <div class="content" v-if="!loading">
      <div v-if="tinkStatus && !tinkStatus.is_premium" class="gk-tip gk-tip--warning gk-tip--premium mb-3">
        <span v-if="premiumTimeLeft > 0" v-translate>
          You have {{ premiumTimeLeft }} days left of your free trial. Please
            <a href="/subscriptions/manage/">consider upgrading</a> to continue using this feature.
        </span>
        <span v-else v-translate>
          The free trial has expired, please <a href="/subscriptions/manage/">consider upgrading</a> to continue using this feature.
        </span>
      </div>
      <BankTransactions v-if="showTransactions" @update-transactions="updateTransactions" ref="BankTransactions"/>
      <div v-else class="content--inner">
        <EmptyState>
          <template v-slot:image>
            <img src="/static/img/empty-states/empty-state-cost.png">
          </template>
          <template v-slot:title>
            {{ $gettext('You dont have any bank connection configured.') }}
          </template>
          <template v-slot:content>
            {{ $gettext('Do you want to integrate you bank transactions in your accounting?') }}
          </template>
          <template v-slot:cta>
            <GekkoButton 
              @click="showModal = !showModal" 
              :text="$gettext('Add new connection')"
              icon="fa-solid fa-plus"
            />
          </template>
        </EmptyState>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import TitleBar from "@/components/TitleBar";
import BankConnectionModal from "@/components/bank/ui/BankConnectionModal";
import BankAccountDeleteModal from "@/components/bank/ui/BankAccountDeleteModal";
import BankTransactions from "@/components/bank/BankTransactions";
import GekkoButton from "@/components/GekkoButton";
import EmptyState from "@/components/ui/EmptyState"
import OnboardingIntroLink from "@/components/ui/OnboardingIntroLink"

export default {
  name: "Bank",
  components: {
    TitleBar,
    BankTransactions,
    BankConnectionModal,
    BankAccountDeleteModal,
    GekkoButton,
    EmptyState,
    OnboardingIntroLink
  },
  props: {

  },
  data () {
    return {
      loading: true,
      showModal: false,
      showDeleteModal: false,
      showTransactions: true,
      showWarningButton: false,
      showRefreshMessage: false,
      showFailureMessage: false,
      existingBeIban: null
    }
  },
  computed: {
    ...mapGetters ({
      tinkStatus: "bankStore/getTinkStatus",
      getBankAccount: "bankStore/getBankAccount",
      bankAccounts: "bankStore/getBankAccounts",
      selectedAccount: "bankStore/getSelectedAccount"
    }),
    premiumTimeLeft () {
      return this.secondsToDhms(this.tinkStatus.premium_time_left)
    },
  },
  watch: {
    bankAccounts () {
      this.clearMessagesIfEmptyList();

      this.bankAccounts.forEach((bankAccount) => {
          this.checkDisplayMessages(bankAccount);
      });
    }
  },
  created () {
    this.loadBankAccounts().then(() => {
      this.onBankAccountsLoaded();
    })
  },
  methods: {
    ...mapMutations({ setSelectedAccount: 'bankStore/SET_SELECTED_ACCOUNT' }),
    ...mapActions ({
      loadTinkStatus: "bankStore/loadTinkStatus",
      loadBankAccounts: "bankStore/loadBankAccounts",
      updateTransactions: "bankStore/updateTransactions",
    }),
    clearMessagesIfEmptyList() {
      if (this.bankAccounts.length < 1) {
        this.showTransactions = false;
        this.showModal = true;
        this.showRefreshMessage = false;
        this.showWarningButton = false;
        this.showFailureMessage = false;
      }
    },
    onBankAccountsLoaded() {
      this.loading = false;
      this.clearMessagesIfEmptyList();
    },
    checkDisplayMessages(bankAccount) {
      this.showWarningButton = false;
      this.showRefreshMessage = false;

      if (bankAccount.unifiedpostpaymentaccount && bankAccount.unifiedpostpaymentaccount.unifiedpostconsent) {
        this.existingBeIban = bankAccount.unifiedpostpaymentaccount.iban;

        let expiryDate = Date.parse(bankAccount.unifiedpostpaymentaccount.unifiedpostconsent.expiry_date);
        let today = new Date();
        var diff = Math.abs(expiryDate-today);
        let days = diff/(1000 * 3600 * 24);

        this.showWarningButton = days < 7; // if there are less than 7 days left, show the message

        this.showFailureMessage = bankAccount.unifiedpostpaymentaccount.unifiedpostconsent.status !== "SUCCESS";

        if (bankAccount.unifiedpostpaymentaccount.last_time_refresh_requested) {

          let lastTimeRefreshRequested = Date.parse(bankAccount.unifiedpostpaymentaccount.last_time_refresh_requested);
          diff = Math.abs(today - lastTimeRefreshRequested);
          days = diff/(1000 * 3600 * 24);

          this.showRefreshMessage = days < 0.5; // less than half a day has passed
        }
      } else if (!bankAccount.unifiedpostpaymentaccount && !bankAccount.tink_credential) {
        this.showFailureMessage = true;
      }
    },
    tryUpdateTransactions () {
      this.loading = true;
      this.updateTransactions().then((response) => {
        if (response === 'showModal') this.showModal = true;
        else {
          this.checkDisplayMessages(this.selectedAccount);

          this.loadBankAccounts().then(() => {
            this.onBankAccountsLoaded();
          });
        }
      }).catch(() => {
        this.$store.commit('setAlert', {
            title: this.$gettext("Error"),
            text: this.$gettext("We could not request an update on your transaction list."),
            variant: 'danger',
            duration: 4
          });
        this.loading = false;
      });
    },
    exportAsExcel () {
      let url = window.Urls['banks:banks_export_csv'](this.selectedAccount.id)
      url = url + "?" + this.encodeFilterData();
      location.href = url
    },
    encodeFilterData () {
      const filters = this.$store.getters["filterStore/getFilter"]
      const str = [];
      for (var prop in filters) {
        if (Object.prototype.hasOwnProperty.call(filters, prop)) {
          str.push(encodeURIComponent(prop) + "=" + encodeURIComponent(filters[prop]));
        }
      }
      return str.join("&");
    },
    secondsToDhms (time) {
      if (!time || time < 0){
        return 0
      }
      time = Math.abs(Number(time))
      return Math.floor(time / (3600 * 24)) + 1 + ''
    }
  }
}
</script>
<style scoped lang="scss">

</style>
